import { BaseModel } from './base.model';
import type {
  SummaryResponse,
  SummaryData,
  SummaryTopic,
  SummaryEntity,
  SummaryStatus,
  EncryptedPayload,
  PersonSummary,
  ContextualFinding,
  RiskAssessment,
  RedFlag,
  DataGap,
  RiskLevel,
} from "~/types/global";
import { getHighestRiskLevel } from "_@snap/reports/helpers/risk-level.helper";

/**
 * A simplified, normalized representation of a summary entity
 */
export interface NormalizedSummaryEntity {
  type: string;
  name: string;
}

/**
 * A simplified, normalized representation of a summary topic
 */
export interface NormalizedSummaryTopic {
  id: string;
  key?: string;
  title: string;
  summaryText?: string;
  confidence?: number;
  tags: string[];
  entities: NormalizedSummaryEntity[];
  relatedSections: string[];
  summaryBySection?: Record<string, string>;
  nestedSummaryBySection?: Record<string, PersonSummary>;
  parentTopicId?: string;
}

/**
 * A simplified, normalized representation of summary data
 */
export interface NormalizedSummaryData {
  executiveSummary?: string;
  topics: NormalizedSummaryTopic[];
  riskAssessment?: RiskAssessment;
  redFlags?: RedFlag[];
  complianceObservations?: string[];
  recommendedNextSteps?: string[];
  dataGaps?: DataGap[];
}

/**
 * A simplified, normalized representation of a summary for the UI
 */
export interface NormalizedSummary {
  summaryId: string;
  reportId: string;
  reportName: string;
  summaryStatus: SummaryStatus;
  data: NormalizedSummaryData | null;
  errorMessage: string | null | undefined;
  errorCode: string | null | undefined;
}

/**
 * Wrapper around SummaryEntity, exposing easy-to-use getters
 */
export class SummaryEntityModel implements BaseModel<NormalizedSummaryEntity> {
  constructor(public readonly raw: SummaryEntity) { }

  get type(): string {
    return this.raw.type || "unknown";
  }

  get name(): string {
    return this.raw.name || "";
  }

  toJSON(): NormalizedSummaryEntity {
    return {
      type: this.type,
      name: this.name,
    };
  }
}

/**
 * Wrapper around SummaryTopic, exposing easy-to-use getters
 */
export class SummaryTopicModel implements BaseModel<NormalizedSummaryTopic> {
  private entityModels: SummaryEntityModel[];

  constructor(public readonly raw: SummaryTopic) {
    this.entityModels = (raw.entities || []).map(e => new SummaryEntityModel(e));
  }

  get id(): string {
    return this.raw.id || "";
  }

  get key(): string {
    return this.raw.key || "";
  }

  get title(): string {
    return this.raw.title || "";
  }

  get summaryText(): string {
    return this.raw.summary_text || "";
  }

  get confidence(): number {
    return this.raw.confidence ?? 0;
  }

  get tags(): string[] {
    return this.raw.tags || [];
  }

  get entities(): SummaryEntityModel[] {
    return this.entityModels;
  }

  get relatedSections(): string[] {
    return this.raw.related_sections || [];
  }

  get summaryBySection(): Record<string, string> | undefined {
    // Always returns the flat version for SummaryDialog compatibility.
    // The DTO layer ensures this field is flat (Record<string, string>) after expansion.
    return this.raw.summary_by_section as Record<string, string> | undefined;
  }

  get nestedSummaryBySection(): Record<string, PersonSummary> | undefined {
    // The DTO layer preserves the original nested structure in _nested_summary_by_section
    // on the parent topic for Multiplos_Renderer to use for inline per-person summaries.
    return (this.raw as any)._nested_summary_by_section as Record<string, PersonSummary> | undefined;
  }

  get parentTopicId(): string | undefined {
    // The DTO layer adds _parentTopicId to child topics to indicate hierarchy in the sidebar.
    return (this.raw as any)._parentTopicId as string | undefined;
  }

  get hasMultipleSections(): boolean {
    return this.relatedSections.length > 1;
  }

  get hasSummaryBySection(): boolean {
    return Boolean(this.summaryBySection && Object.keys(this.summaryBySection).length > 0);
  }

  get contextualFindings(): ContextualFinding[] {
    return this.raw.contextual_findings || [];
  }

  get highestRiskLevel(): RiskLevel | undefined {
    return getHighestRiskLevel(this.contextualFindings);
  }

  toJSON(): NormalizedSummaryTopic {
    return {
      id: this.id,
      key: this.key || undefined,
      title: this.title,
      summaryText: this.summaryText || undefined,
      confidence: this.raw.confidence,
      tags: this.tags,
      entities: this.entities.map(e => e.toJSON()),
      relatedSections: this.relatedSections,
      summaryBySection: this.summaryBySection,
      nestedSummaryBySection: this.nestedSummaryBySection,
      parentTopicId: this.parentTopicId,
    };
  }
}

/**
 * Wrapper around SummaryData, exposing easy-to-use getters
 */
export class SummaryDataModel implements BaseModel<NormalizedSummaryData> {
  private topicModels: SummaryTopicModel[];

  constructor(public readonly raw: SummaryData) {
    this.topicModels = (raw.topics || []).map(t => new SummaryTopicModel(t));
  }

  get executiveSummary(): string | undefined {
    return this.raw.executive_summary;
  }

  get topics(): SummaryTopicModel[] {
    return this.topicModels;
  }

  get topicsCount(): number {
    return this.topicModels.length;
  }

  get hasTopics(): boolean {
    return this.topicsCount > 0;
  }

  /** Get all unique tags from all topics */
  get allTags(): string[] {
    const tagSet = new Set<string>();
    this.topicModels.forEach(topic => {
      topic.tags.forEach(tag => tagSet.add(tag));
    });
    return Array.from(tagSet);
  }

  /** Get all unique entities from all topics */
  get allEntities(): SummaryEntityModel[] {
    const entityMap = new Map<string, SummaryEntityModel>();
    this.topicModels.forEach(topic => {
      topic.entities.forEach(entity => {
        const key = `${entity.type}:${entity.name}`;
        if (!entityMap.has(key)) {
          entityMap.set(key, entity);
        }
      });
    });
    return Array.from(entityMap.values());
  }

  /** Filter topics by tag */
  getTopicsByTag(tag: string): SummaryTopicModel[] {
    return this.topicModels.filter(topic => topic.tags.includes(tag));
  }

  /** Filter topics by minimum confidence */
  getTopicsByMinConfidence(minConfidence: number): SummaryTopicModel[] {
    return this.topicModels.filter(topic => topic.confidence >= minConfidence);
  }

  get riskAssessment(): RiskAssessment | undefined {
    return this.raw.risk_assessment;
  }

  get redFlags(): RedFlag[] {
    return this.raw.red_flags || [];
  }

  get complianceObservations(): string[] {
    return this.raw.compliance_observations || [];
  }

  get recommendedNextSteps(): string[] {
    return this.raw.recommended_next_steps || [];
  }

  get dataGaps(): DataGap[] {
    return this.raw.data_gaps || [];
  }

  toJSON(): NormalizedSummaryData {
    return {
      executiveSummary: this.executiveSummary,
      topics: this.topics.map(t => t.toJSON()),
      riskAssessment: this.riskAssessment,
      redFlags: this.redFlags.length > 0 ? this.redFlags : undefined,
      complianceObservations: this.complianceObservations.length > 0 ? this.complianceObservations : undefined,
      recommendedNextSteps: this.recommendedNextSteps.length > 0 ? this.recommendedNextSteps : undefined,
      dataGaps: this.dataGaps.length > 0 ? this.dataGaps : undefined,
    };
  }
}

/**
 * Wrapper around SummaryResponse, exposing easy-to-use getters
 */
export class SummaryModel implements BaseModel<NormalizedSummary> {
  private dataModel: SummaryDataModel | null;

  constructor(public readonly raw: SummaryResponse) {
    this.dataModel = this.createDataModel();
  }

  private createDataModel(): SummaryDataModel | null {
    const data = this.raw.data;

    // If data is encrypted or null, return null
    if (!data || this.isEncrypted(data)) {
      return null;
    }

    // If data is plain SummaryData
    return new SummaryDataModel(data as SummaryData);
  }

  private isEncrypted(value: any): value is EncryptedPayload {
    return (
      value != null &&
      typeof value === "object" &&
      "encrypted" in value &&
      "iv" in value &&
      typeof value.encrypted === "string" &&
      typeof value.iv === "string"
    );
  }

  get summaryId(): string {
    return this.raw.summary_id || "";
  }

  get reportId(): string {
    return this.raw.report_id || "";
  }

  get reportName(): string {
    const name = this.raw.report_name;
    if (typeof name === "string") {
      return name;
    }
    // If encrypted, return default
    return "Relatório";
  }

  get summaryStatus(): SummaryStatus {
    return this.raw.summary_status || null;
  }

  get data(): SummaryDataModel | null {
    return this.dataModel;
  }

  get isSuccess(): boolean {
    return this.summaryStatus === "success";
  }

  get isPending(): boolean {
    return this.summaryStatus === "pending";
  }

  get isError(): boolean {
    return this.summaryStatus === "error";
  }

  get errorMessage(): string | null | undefined {
    return this.raw.error_message ?? undefined;
  }

  get errorCode(): string | null | undefined {
    return this.raw.error_code ?? undefined;
  }

  get hasData(): boolean {
    return this.data !== null && this.data.hasTopics;
  }

  get topicsCount(): number {
    return this.data?.topicsCount || 0;
  }

  toJSON(): NormalizedSummary {
    return {
      summaryId: this.summaryId,
      reportId: this.reportId,
      reportName: this.reportName,
      summaryStatus: this.summaryStatus,
      data: this.data?.toJSON() || null,
      errorMessage: this.errorMessage,
      errorCode: this.errorCode,
    };
  }
}
